import React, {useCallback, useEffect, useLayoutEffect, useMemo, useState} from 'react';
import {AnyReceiveMethodName, AppearanceType, VKBridgeEvent} from '@vkontakte/vk-bridge';
import {AdaptivityProvider, AppRoot, ConfigProvider, Placeholder, SizeType, Spinner} from '@vkontakte/vkui';
import {APP_DESKTOP_WIDTH} from 'utils/constants';
import {useLeadFormData} from 'api/services/leads';
import useAnalytics from 'utils/hooks/useAnalytics';
import {AppearanceContextProvider, useAppearance} from 'utils/appearanceContext';
import {
    formatResourceErrorEntry,
    formatResourceTimingEntry,
    isBls,
    isBusinessCard,
    isLeadForm,
    isMobile,
    isOKClient,
    isSdkClient,
    isSurvey,
} from 'utils/utils';
import {FormDataType} from 'api/types/LeadForm';
import {UserInfoContextCreater} from 'utils/userInfoContext';
import {SearchParamsContextCreater, useSearchBaseParams} from 'utils/searchParamsContext';
import {VIEW} from 'utils/types';
import AppLeadForm from './AppLeadForm';
import AppSurvey from './AppSurvey';
import AppYClients from './AppYClients';
import {Icon56ErrorOutline} from '@vkontakte/icons';
import {bridge} from 'utils/bridge';

import '@vkontakte/vkui/dist/vkui.css';
import './app.css';
import AppBusinessCard from './AppBusinessCard';
import AppBls from './AppBls';

const AppDataComponent: React.FC = () => {
    const [data, setData] = useState<FormDataType | null>(null);
    const [isDataInited, setIsDataInited] = useState(false);
    const [forceShowCloseIcon, setForceShowCloseIcon] = useState(false);
    const searchBaseParams = useSearchBaseParams();
    const {formDataHash, view} = searchBaseParams;
    const updateLeadFormData = useLeadFormData(setData);
    const {
        onAppReadyAnalyticEvent,
        onAppResourcesLoadedAnalyticEvent,
        openFormAnalyticEvent,
        hideFormAnalyticEvent,
        restoreFormAnalyticEvent,
        onResourceLoadedAnalyticEvent,
        onResourceLoadingErrorAnalyticEvent,
    } = useAnalytics();

    const onTimingEvent = useCallback(
        (e: LoadResourceTimingEvent) => {
            onResourceLoadedAnalyticEvent(formatResourceTimingEntry(e.detail));
        },
        [onResourceLoadedAnalyticEvent],
    );

    const onErrorEvent = useCallback(
        (e: LoadResourceErrorEvent) => {
            onResourceLoadingErrorAnalyticEvent(formatResourceErrorEntry(e.detail));
        },
        [onResourceLoadingErrorAnalyticEvent],
    );

    useEffect(() => {
        // если что-то пошло не так на этапе инициализации (скорее всего старого браузера), то не будем ничего отправлять
        if (!window.loadResourcesErrors || !window.loadResourcesMetrics) {
            return;
        }

        // сначала перебираем все ресурсы, которые упали заранее
        const errors = window.loadResourcesErrors.getAll();
        for (const error of errors) {
            onResourceLoadingErrorAnalyticEvent(formatResourceErrorEntry(error));
        }

        // сначала перебираем все ресурсы, которые загрузились заранее
        const metrics = window.loadResourcesMetrics.getAll();
        for (const metric of metrics) {
            onResourceLoadedAnalyticEvent(formatResourceTimingEntry(metric));
        }
        // затем слушаем событие, которое вызывается при загрузке ресурсов
        document.addEventListener('preloadTimingAvailable', onTimingEvent);
        // и событие, которое вызывается при ошибке загрузки ресурсов
        document.addEventListener('loadResourcesError', onErrorEvent);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        onAppResourcesLoadedAnalyticEvent();
        openFormAnalyticEvent();

        bridge &&
            isOKClient() &&
            bridge
                .sendWithCatch('VKWebAppGetClientVersion')
                .then((res) => {
                    // в ОКах на ios старше (включая) 10.59 начали читать is_vkui_internal, из-за чего в приле пропал нативный крестик.
                    // поэтому добавляем в этом случае свой
                    const [major, minor] = res.version.split('.').map(Number);
                    const isAllowVersion = !!(major && major >= 10 && (major === 10 ? minor >= 70 : true));
                    if (res.platform === 'ios' && isAllowVersion) {
                        setForceShowCloseIcon(true);
                    }
                })
                .catch(() => {});

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onBridgeEvent = useCallback(
        ({detail: {type}}: VKBridgeEvent<AnyReceiveMethodName>) => {
            if (type === 'VKWebAppViewHide') {
                // внимательно, тк hide вызывается не только после свайпа назад и возврата в ленту, но и при свитче прилы с вк на что-то другое
                // важно, тк при свайпе обратно в ленту и опять в баннер вызовется VKWebAppChangeFragment, а при свитче прил - не вызовется
                hideFormAnalyticEvent();
            } else if (type === 'VKWebAppViewRestore') {
                restoreFormAnalyticEvent();
            }
        },
        [hideFormAnalyticEvent, restoreFormAnalyticEvent],
    );

    useEffect(() => {
        bridge && bridge.subscribe(onBridgeEvent);
        return () => {
            bridge && bridge.unsubscribe(onBridgeEvent);
        };
    }, [onBridgeEvent]);

    useEffect(() => {
        setData(null); // обязательно обнуляем чтоб компонент успела отрисовать лоадер перед след отрисовкой и размаунтить AppLeadForm
        // тк из-за особенностей vkui нельзя просто взять и заменить панели у view
        if (formDataHash) {
            setTimeout(() => updateLeadFormData({...searchBaseParams, formDataHash}), 0);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formDataHash]);

    useEffect(() => {
        if (data && !isDataInited) {
            onAppReadyAnalyticEvent();
            setIsDataInited(true);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [data, isDataInited]);

    const layout = useMemo(() => {
        return (
            view ??
            (!isMobile() && window.innerWidth >= APP_DESKTOP_WIDTH ? VIEW.overlay : bridge ? VIEW.miniapp : VIEW.mobile)
        );
    }, [view]);

    useLayoutEffect(() => {
        layout === VIEW.miniapp && document.body.classList.add('layout_miniapp');
    }, [layout]);

    const isShowCloseBtn = useMemo(() => {
        return forceShowCloseIcon || (layout === VIEW.miniapp && !isOKClient() && !isSdkClient());
    }, [forceShowCloseIcon, layout]);

    if (!formDataHash) {
        return (
            <Placeholder
                stretched
                header='Произошла ошибка'
                icon={<Icon56ErrorOutline />}
            >
                не передан обязательный идендификатор формы
            </Placeholder>
        );
    }

    return data ? (
        <UserInfoContextCreater isFilledPreviously={data.is_filled_previously}>
            {isLeadForm(data) ? (
                <AppLeadForm
                    formData={data}
                    formDataHash={formDataHash}
                    layout={layout}
                    isShowCloseBtn={isShowCloseBtn}
                />
            ) : isBusinessCard(data) ? (
                <AppBusinessCard
                    formData={data}
                    formDataHash={formDataHash}
                    layout={layout}
                    isShowCloseBtn={isShowCloseBtn}
                />
            ) : isSurvey(data) ? (
                <AppSurvey
                    formData={data}
                    layout={layout}
                    isShowCloseBtn={isShowCloseBtn}
                />
            ) : isBls(data) ? (
                <AppBls
                    formData={data}
                    formDataHash={formDataHash}
                    layout={layout}
                    isShowCloseBtn={isShowCloseBtn}
                />
            ) : (
                <AppYClients
                    formData={data}
                    layout={layout}
                    isShowCloseBtn={isShowCloseBtn}
                />
            )}
        </UserInfoContextCreater>
    ) : (
        <Spinner />
    );
};

export const AppInner = () => {
    const appearance = useAppearance();

    return (
        <ConfigProvider
            appearance={appearance}
            isWebView
            // platform='ios' // для тестов
        >
            <AdaptivityProvider sizeY={SizeType.REGULAR}>
                <AppRoot>
                    <AppDataComponent />
                </AppRoot>
            </AdaptivityProvider>
        </ConfigProvider>
    );
};

export interface AppProps {
    initialTheme?: AppearanceType;
}

export const App = ({initialTheme}: AppProps) => {
    return (
        <SearchParamsContextCreater>
            <AppearanceContextProvider initialTheme={initialTheme}>
                <AppInner />
            </AppearanceContextProvider>
        </SearchParamsContextCreater>
    );
};
